import { TPresenceActivity } from './types'

/**
 * Is the user at the computer?
 *
 * Two sources, best one wins:
 *  1. IdleDetector API (Chromium, needs a one time permission): system wide idle time and
 *     screen lock. An agent who works in another window while waiting for calls stays active.
 *  2. Fallback: input inside the dashboard tabs + page visibility. Working elsewhere for longer
 *     than the threshold looks like "away" here, which is why (1) is asked for.
 *
 * A phone call in progress always counts as activity: nobody touches the mouse while talking.
 */

const DEFAULT_IDLE_MINUTES = 10
const CHECK_INTERVAL_MS = 15_000
const INPUT_THROTTLE_MS = 1_000
// IdleDetector refuses thresholds below a minute
const MIN_SYSTEM_IDLE_THRESHOLD_MS = 60_000

const INPUT_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
] as const

export interface IActivitySnapshot {
  activity: TPresenceActivity
  visible: boolean
  lastInputAt: number
}

type TListener = (snapshot: IActivitySnapshot) => void

// lib.dom does not ship IdleDetector yet
interface IIdleDetector extends EventTarget {
  userState: 'active' | 'idle' | null
  screenState: 'locked' | 'unlocked' | null
  start(options: { threshold: number; signal?: AbortSignal }): Promise<void>
}
interface IIdleDetectorConstructor {
  new (): IIdleDetector
  requestPermission(): Promise<'granted' | 'denied'>
}

const getIdleDetector = (): IIdleDetectorConstructor | undefined =>
  (window as unknown as { IdleDetector?: IIdleDetectorConstructor })
    .IdleDetector

export const idleThresholdMs = () => {
  const minutes = Number(window.settings?.PRESENCE_IDLE_MINUTES)

  return (minutes > 0 ? minutes : DEFAULT_IDLE_MINUTES) * 60_000
}

export class ActivityTracker {
  private listeners: TListener[] = []
  private started = false
  private timer?: ReturnType<typeof setInterval>
  private lastInputAt = Date.now()
  private lastInputHandledAt = 0
  private activity: TPresenceActivity = 'active'
  private callsInProgress = 0

  private systemDetector?: IIdleDetector
  private systemDetectorAbort?: AbortController
  private systemIdle = false

  start() {
    if (this.started) return
    this.started = true

    INPUT_EVENTS.forEach((event) =>
      window.addEventListener(event, this.handleInput, {
        passive: true,
        capture: true,
      })
    )
    document.addEventListener('visibilitychange', this.handleVisibility)
    this.timer = setInterval(this.evaluate, CHECK_INTERVAL_MS)

    // no prompt here: it only starts when the permission was given before
    this.startSystemIdleDetection(false)
  }

  stop() {
    if (!this.started) return
    this.started = false

    INPUT_EVENTS.forEach((event) =>
      window.removeEventListener(event, this.handleInput, { capture: true })
    )
    document.removeEventListener('visibilitychange', this.handleVisibility)
    if (this.timer) clearInterval(this.timer)
    this.systemDetectorAbort?.abort()
    this.systemDetector = undefined
  }

  subscribe(listener: TListener) {
    this.listeners.push(listener)

    return () => {
      this.listeners = this.listeners.filter((item) => item !== listener)
    }
  }

  snapshot(): IActivitySnapshot {
    return {
      activity: this.activity,
      visible: document.visibilityState === 'visible',
      lastInputAt: this.lastInputAt,
    }
  }

  /** How long the user has been idle for, 0 while active */
  idleForMs() {
    return this.activity === 'idle' ? Date.now() - this.lastInputAt : 0
  }

  /** Something that proves the user is there, but is not a DOM input (e.g. answering a call) */
  ping() {
    this.lastInputAt = Date.now()
    this.evaluate()
  }

  /** A call in progress keeps the user active until it ends */
  setCallInProgress(inProgress: boolean) {
    this.callsInProgress = Math.max(
      0,
      this.callsInProgress + (inProgress ? 1 : -1)
    )
    this.ping()
  }

  isSystemIdleDetectionAvailable() {
    return Boolean(getIdleDetector())
  }

  isSystemIdleDetectionActive() {
    return Boolean(this.systemDetector)
  }

  /**
   * Must be called from a user gesture (a click): browsers only show the permission prompt then
   */
  async enableSystemIdleDetection(): Promise<boolean> {
    return this.startSystemIdleDetection(true)
  }

  private async startSystemIdleDetection(
    askPermission: boolean
  ): Promise<boolean> {
    const IdleDetector = getIdleDetector()
    if (!IdleDetector || this.systemDetector) {
      return Boolean(this.systemDetector)
    }

    try {
      if (askPermission) {
        if ((await IdleDetector.requestPermission()) !== 'granted') return false
      } else {
        const permission = await navigator.permissions.query({
          name: 'idle-detection' as PermissionName,
        })
        if (permission.state !== 'granted') return false
      }

      const detector = new IdleDetector()
      const abort = new AbortController()

      detector.addEventListener('change', () => {
        this.systemIdle =
          detector.userState === 'idle' || detector.screenState === 'locked'
        if (!this.systemIdle) this.lastInputAt = Date.now()
        this.evaluate()
      })

      await detector.start({
        threshold: Math.max(MIN_SYSTEM_IDLE_THRESHOLD_MS, idleThresholdMs()),
        signal: abort.signal,
      })

      this.systemDetector = detector
      this.systemDetectorAbort = abort
      this.evaluate()

      return true
    } catch (e) {
      // unsupported, blocked by a permissions policy, ...: the fallback keeps working
      console.debug('presence: system idle detection is not available', e)

      return false
    }
  }

  private handleInput = () => {
    const now = Date.now()
    if (now - this.lastInputHandledAt < INPUT_THROTTLE_MS) return

    this.lastInputHandledAt = now
    this.lastInputAt = now
    if (this.activity === 'idle') this.evaluate()
  }

  private handleVisibility = () => {
    // coming back to the tab is input; leaving it is not
    if (document.visibilityState === 'visible') this.lastInputAt = Date.now()
    this.notify()
  }

  private evaluate = () => {
    const activity = this.resolveActivity()

    if (activity !== this.activity) {
      this.activity = activity
      this.notify()
    }
  }

  private resolveActivity(): TPresenceActivity {
    if (this.callsInProgress > 0) return 'active'

    if (this.systemDetector) {
      // keep `lastInputAt` honest while the user works in another window
      if (!this.systemIdle) this.lastInputAt = Date.now()

      return this.systemIdle ? 'idle' : 'active'
    }

    return Date.now() - this.lastInputAt > idleThresholdMs() ? 'idle' : 'active'
  }

  private notify() {
    const snapshot = this.snapshot()
    this.listeners.forEach((listener) => listener(snapshot))
  }
}
